/**
 * Authored `elevation` is metres above the immediate support (innermost
 * containing land, or water). World elevation is that stack sum — the top
 * face, shade, and paint order use it. Walls use `baseElevation` (the
 * support plateau, or the water) so a terrace is a riser, not a pillar
 * through the land it sits on. No parent id is stored.
 */

import { signedArea } from '../geometry.ts';
import { clampElevation } from './document.ts';
import { buildPlateauProbe, exposedNorthWaterlineRuns, type PlateauProbe } from './extrude.ts';
import { pointInPolygon } from './islands.ts';
import { captureRingSignature, ringSignatureMatches, tessellateRing } from './ring.ts';
import { MIN_RING_VERTICES, type MapDocument, type Ring, type Vec2 } from './types.ts';

export type ElevationStackEntry = {
  supportId: string | null;
  worldElevation: number;
  /** World height of the support plateau; 0 if this body meets the water. */
  baseElevation: number;
  /** Offset cap + extruded AABB; rebuilt with the stack, not per hover sample. */
  plateau: PlateauProbe;
};

export type StackableBody = {
  id: string;
  elevation: number;
} & Ring;

type PreparedBody = {
  id: string;
  elevation: number;
  outline: readonly Vec2[];
  area: number;
};

function stackableBodies(map: Pick<MapDocument, 'islands' | 'rocks'>): StackableBody[] {
  return [...map.islands, ...map.rocks];
}

/** Placeholder until `attachPlateaus` writes the real probe on the same entry. */
const emptyPlateau: PlateauProbe = {
  footprint: [],
  top: [],
  box: null,
  elevation: 0,
};

type MapStackCache = {
  islandIds: readonly string[];
  rockIds: readonly string[];
  elevations: Float64Array;
  ringSignatures: readonly Float64Array[];
  stack: Map<string, ElevationStackEntry>;
  hintRuns: Map<string, Vec2[][]>;
  plateaus: readonly PlateauProbe[];
};

const stackCache = new WeakMap<MapDocument, MapStackCache>();

let stackCalls = 0;
let stackBuilds = 0;
let hintRunCalls = 0;
let hintRunBuilds = 0;

/**
 * Counters behind the zoom-redraw cache guards. `builds` is a stack actually
 * resolved (or hint runs actually walked). `calls` is requests. Nothing reads
 * them in production.
 */
export function elevationStackStats(): { calls: number; builds: number } {
  return { calls: stackCalls, builds: stackBuilds };
}

export function waterlineHintStats(): { calls: number; builds: number } {
  return { calls: hintRunCalls, builds: hintRunBuilds };
}

function mapStackSignatureMatches(map: MapDocument, cached: MapStackCache): boolean {
  if (
    cached.islandIds.length !== map.islands.length ||
    cached.rockIds.length !== map.rocks.length
  ) {
    return false;
  }

  for (let i = 0; i < map.islands.length; i += 1) {
    const island = map.islands[i];
    const signature = cached.ringSignatures[i];

    if (
      !island ||
      !signature ||
      cached.islandIds[i] !== island.id ||
      cached.elevations[i] !== island.elevation ||
      !ringSignatureMatches(island, signature)
    ) {
      return false;
    }
  }

  const rockOffset = map.islands.length;

  for (let i = 0; i < map.rocks.length; i += 1) {
    const rock = map.rocks[i];
    const signature = cached.ringSignatures[rockOffset + i];

    if (
      !rock ||
      !signature ||
      cached.rockIds[i] !== rock.id ||
      cached.elevations[rockOffset + i] !== rock.elevation ||
      !ringSignatureMatches(rock, signature)
    ) {
      return false;
    }
  }

  return true;
}

function attachPlateaus(
  map: MapDocument,
  stack: Map<string, ElevationStackEntry>,
): PlateauProbe[] {
  const plateaus: PlateauProbe[] = [];

  for (const body of stackableBodies(map)) {
    const entry = stack.get(body.id);

    if (!entry) {
      continue;
    }

    entry.plateau = buildPlateauProbe(tessellateRing(body), entry.worldElevation);
    plateaus.push(entry.plateau);
  }

  return plateaus;
}

function captureMapStack(map: MapDocument, stack: Map<string, ElevationStackEntry>): MapStackCache {
  const bodies = stackableBodies(map);
  const elevations = new Float64Array(bodies.length);
  const ringSignatures = bodies.map((body, i) => {
    elevations[i] = body.elevation;
    return captureRingSignature(body);
  });

  return {
    islandIds: map.islands.map((island) => island.id),
    rockIds: map.rocks.map((rock) => rock.id),
    elevations,
    ringSignatures,
    stack,
    hintRuns: new Map(),
    plateaus: attachPlateaus(map, stack),
  };
}

function cachedMapStack(map: MapDocument): MapStackCache {
  stackCalls += 1;
  const cached = stackCache.get(map);

  if (cached && mapStackSignatureMatches(map, cached)) {
    return cached;
  }

  stackBuilds += 1;
  const next = captureMapStack(map, stackFromBodies(stackableBodies(map)));
  stackCache.set(map, next);
  return next;
}

function hintRunsForBody(map: MapDocument, bodyId: string): Vec2[][] {
  hintRunCalls += 1;
  const cached = cachedMapStack(map);
  const hit = cached.hintRuns.get(bodyId);

  if (hit) {
    return hit;
  }

  hintRunBuilds += 1;
  const entry = cached.stack.get(bodyId);

  if (!entry || entry.supportId !== null) {
    const empty: Vec2[][] = [];
    cached.hintRuns.set(bodyId, empty);
    return empty;
  }

  const body = stackableBodies(map).find((item) => item.id === bodyId);

  if (!body) {
    const empty: Vec2[][] = [];
    cached.hintRuns.set(bodyId, empty);
    return empty;
  }

  const runs = exposedNorthWaterlineRuns(tessellateRing(body), entry.worldElevation);
  cached.hintRuns.set(bodyId, runs);
  return runs;
}

function prepare(bodies: readonly StackableBody[]): PreparedBody[] {
  return bodies.map((body) => {
    const outline = tessellateRing(body);
    return {
      id: body.id,
      elevation: body.elevation,
      outline,
      area: Math.abs(signedArea(outline)),
    };
  });
}

/** Outer fully contains inner when every inner outline vertex sits inside it. */
export function outlineContainsOutline(
  outer: readonly Vec2[],
  inner: readonly Vec2[],
): boolean {
  if (inner.length < MIN_RING_VERTICES || outer.length < MIN_RING_VERTICES) {
    return false;
  }

  const outerArea = Math.abs(signedArea(outer));
  const innerArea = Math.abs(signedArea(inner));

  if (outerArea <= innerArea || innerArea < 1e-6) {
    return false;
  }

  return inner.every((point) => pointInPolygon(point, outer));
}

export function ringContainsRing(outer: Ring, inner: Ring): boolean {
  return outlineContainsOutline(tessellateRing(outer), tessellateRing(inner));
}

function fullyContains(outer: PreparedBody, inner: PreparedBody): boolean {
  if (outer.id === inner.id) {
    return false;
  }

  return outlineContainsOutline(outer.outline, inner.outline);
}

function immediateSupportId(
  body: PreparedBody,
  others: readonly PreparedBody[],
): string | null {
  let best: PreparedBody | null = null;

  for (const candidate of others) {
    if (!fullyContains(candidate, body)) {
      continue;
    }

    if (
      !best ||
      candidate.area < best.area ||
      (candidate.area === best.area && candidate.id < best.id)
    ) {
      best = candidate;
    }
  }

  return best?.id ?? null;
}

export function resolveSupportIds(bodies: readonly StackableBody[]): Map<string, string | null> {
  const prepared = prepare(bodies);
  const supports = new Map<string, string | null>();

  for (const body of prepared) {
    supports.set(body.id, immediateSupportId(body, prepared));
  }

  return supports;
}

function stackFromBodies(bodies: readonly StackableBody[]): Map<string, ElevationStackEntry> {
  const supports = resolveSupportIds(bodies);
  const byId = new Map(bodies.map((body) => [body.id, body]));
  const resolved = new Map<string, ElevationStackEntry>();
  const visiting = new Set<string>();

  const worldOf = (id: string): number => {
    const existing = resolved.get(id);

    if (existing) {
      return existing.worldElevation;
    }

    const authored = byId.get(id)?.elevation ?? 0;

    if (visiting.has(id)) {
      const entry: ElevationStackEntry = {
        supportId: null,
        worldElevation: clampElevation(authored),
        baseElevation: 0,
        plateau: emptyPlateau,
      };
      resolved.set(id, entry);
      return entry.worldElevation;
    }

    visiting.add(id);
    const supportId = supports.get(id) ?? null;
    const baseElevation = supportId ? worldOf(supportId) : 0;
    const world = clampElevation(authored + baseElevation);
    visiting.delete(id);
    const entry: ElevationStackEntry = {
      supportId,
      worldElevation: world,
      baseElevation,
      plateau: emptyPlateau,
    };
    resolved.set(id, entry);
    return world;
  };

  for (const body of bodies) {
    worldOf(body.id);
  }

  return resolved;
}

export function resolveElevationStack(map: MapDocument): Map<string, ElevationStackEntry> {
  return cachedMapStack(map).stack;
}

/** Cached under-cap probes for every island and rock. Shared — treat as read-only. */
export function plateauProbesForMap(map: MapDocument): readonly PlateauProbe[] {
  return cachedMapStack(map).plateaus;
}

export function worldElevation(map: MapDocument, id: string): number {
  return resolveElevationStack(map).get(id)?.worldElevation ?? 0;
}

export function bodyMeetsWater(map: MapDocument, id: string): boolean {
  const entry = resolveElevationStack(map).get(id);
  return entry !== undefined && entry.supportId === null;
}

/**
 * Pre-v11 files stored metres above water. Rewrite each authored value to the
 * extra height above its support so world height stays the same.
 */
export function absoluteElevationsToRelative(bodies: readonly StackableBody[]): void {
  const supports = resolveSupportIds(bodies);
  const absolute = new Map(bodies.map((body) => [body.id, body.elevation]));

  for (const body of bodies) {
    const supportId = supports.get(body.id);
    const supportAbs = supportId ? (absolute.get(supportId) ?? 0) : 0;
    body.elevation = clampElevation(Math.max(0, (absolute.get(body.id) ?? 0) - supportAbs));
  }
}

/** Hidden-shore hint geometry for a body that actually meets the water. */
export function waterlineHintRuns(map: MapDocument, bodyId: string): Vec2[][] {
  return hintRunsForBody(map, bodyId);
}

/** Cached hint runs for every island and rock. Shared — treat values as read-only. */
export function waterlineHintRunsById(map: MapDocument): ReadonlyMap<string, Vec2[][]> {
  const cached = cachedMapStack(map);

  for (const body of stackableBodies(map)) {
    if (!cached.hintRuns.has(body.id)) {
      hintRunsForBody(map, body.id);
    }
  }

  return cached.hintRuns;
}
